import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CustomTheme } from '../themes/presets';
import { defaultTheme, themePresets } from '../themes/presets';
import { applyTheme, setupThemeSync, setupSystemThemeListener } from '../themes/theme-utils';

interface CustomThemeState {
  customTheme: CustomTheme;
  presets: CustomTheme[];
  setTheme: (theme: CustomTheme) => void;
  updateColors: (colors: Partial<CustomTheme['colors']>) => void;
  updateMode: (mode: CustomTheme['mode']) => void;
  updateFontSize: (fontSize: CustomTheme['fontSize']) => void;
  updateDensity: (density: CustomTheme['density']) => void;
  updateBorderRadius: (borderRadius: CustomTheme['borderRadius']) => void;
  updateAnimationSpeed: (animationSpeed: CustomTheme['animationSpeed']) => void;
  resetToDefault: () => void;
  loadPreset: (presetId: string) => void;
  createCustomTheme: (name: string, basePreset?: string) => void;
}

export const useCustomThemeStore = create<CustomThemeState>()(
  persist(
    (set, get) => ({
      customTheme: defaultTheme,
      presets: themePresets,

      setTheme: (theme) => {
        applyTheme(theme);
        set({ customTheme: theme });
      },

      updateColors: (colors) => {
        const current = get().customTheme;
        const updated: CustomTheme = {
          ...current,
          id: 'custom',
          name: current.id === 'custom' ? current.name : 'Custom Theme',
          colors: { ...current.colors, ...colors },
        };
        get().setTheme(updated);
      },

      updateMode: (mode) => {
        const current = get().customTheme;
        const updated: CustomTheme = { ...current, mode };
        get().setTheme(updated);
      },

      updateFontSize: (fontSize) => {
        const current = get().customTheme;
        const updated: CustomTheme = { ...current, fontSize };
        get().setTheme(updated);
      },

      updateDensity: (density) => {
        const current = get().customTheme;
        const updated: CustomTheme = { ...current, density };
        get().setTheme(updated);
      },

      updateBorderRadius: (borderRadius) => {
        const current = get().customTheme;
        const updated: CustomTheme = { ...current, borderRadius };
        get().setTheme(updated);
      },

      updateAnimationSpeed: (animationSpeed) => {
        const current = get().customTheme;
        const updated: CustomTheme = { ...current, animationSpeed };
        get().setTheme(updated);
      },

      resetToDefault: () => {
        get().setTheme(defaultTheme);
      },

      loadPreset: (presetId) => {
        const preset = themePresets.find((p) => p.id === presetId);
        if (preset) {
          const current = get().customTheme;
          get().setTheme({
            ...current,
            id: preset.id,
            name: preset.name,
            colors: { ...preset.colors },
          });
        }
      },

      createCustomTheme: (name, basePreset = 'default') => {
        const base = themePresets.find((p) => p.id === basePreset) || defaultTheme;
        const custom: CustomTheme = {
          ...base,
          id: 'custom',
          name,
        };
        get().setTheme(custom);
      },
    }),
    {
      name: 'custom-theme-storage',
      partialize: (state) => ({
        customTheme: state.customTheme,
        presets: state.presets,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.customTheme);
        }
      },
    }
  )
);

// Initialize theme on load and set up listeners
if (typeof window !== 'undefined') {
  // Apply theme immediately
  const state = useCustomThemeStore.getState();
  applyTheme(state.customTheme);

  // Sync theme across tabs (store cleanup for HMR)
  const cleanupSync = setupThemeSync((theme) => {
    useCustomThemeStore.setState({ customTheme: theme });
    applyTheme(theme);
  });

  // Listen for system theme changes (store cleanup for HMR)
  const cleanupSystem = setupSystemThemeListener(() => {
    const { customTheme } = useCustomThemeStore.getState();
    if (customTheme.mode === 'system') {
      applyTheme(customTheme);
    }
  });

  // Clean up on HMR
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      cleanupSync();
      cleanupSystem();
    });
  }
}
